/**
 * GLITCH Theme — Featured Collections Carousel
 *
 * Custom element handling:
 *  - Auto-scroll one item at a time (configurable speed)
 *  - Pause on hover (optional)
 *  - Pause on user interaction (touch/scroll/wheel)
 *  - Smooth loop back to start when reaching end
 *  - Arrow button navigation with boundary detection
 *  - Native touch swipe (via CSS overflow-x: auto)
 *  - Pause when out of viewport (perf)
 *  - Respects prefers-reduced-motion
 */

class FeaturedCollectionsCarousel extends HTMLElement {
  constructor() {
    super();
    this.autoscrollTimer = null;
    this.resumeTimer = null;
    this.isPaused = false;
    this.isVisible = true;
    this.RESUME_DELAY = 3000; // resume autoscroll 3s after last interaction
  }

  connectedCallback() {
    this.list = this.querySelector('.featured-collections__list');
    this.items = Array.from(this.querySelectorAll('.featured-collections__item'));
    this.prevButton = this.querySelector('.featured-collections__arrow--prev');
    this.nextButton = this.querySelector('.featured-collections__arrow--next');

    if (!this.list || this.items.length === 0) return;

    this.autoscrollEnabled = this.dataset.autoscroll === 'true';
    this.autoscrollSpeed = (parseInt(this.dataset.autoscrollSpeed, 10) || 4) * 1000;
    this.pauseOnHover = this.dataset.pauseOnHover === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.bindEvents();
    this.updateArrowStates();
    this.setupIntersectionObserver();

    if (this.autoscrollEnabled && !this.reducedMotion) {
      this.startAutoscroll();
    }
  }

  disconnectedCallback() {
    this.stopAutoscroll();
    if (this.observer) this.observer.disconnect();
  }

  bindEvents() {
    // Arrow navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.scrollByItems(-1);
        this.handleUserInteraction();
      });
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.scrollByItems(1);
        this.handleUserInteraction();
      });
    }

    // Pause autoscroll when user is actively scrolling/swiping
    this.list.addEventListener('touchstart', () => this.handleUserInteraction(), { passive: true });
    this.list.addEventListener('wheel', () => this.handleUserInteraction(), { passive: true });

    // Update arrow disabled states as user scrolls
    this.list.addEventListener('scroll', this.throttle(() => this.updateArrowStates(), 100));

    // Pause on hover (desktop)
    if (this.pauseOnHover) {
      this.addEventListener('mouseenter', () => {
        this.isPaused = true;
        this.stopAutoscroll();
      });
      this.addEventListener('mouseleave', () => {
        this.isPaused = false;
        this.resumeAutoscrollIfAllowed();
      });
    }

    // Pause when window loses focus
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoscroll();
      } else {
        this.resumeAutoscrollIfAllowed();
      }
    });
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting;
          if (entry.isIntersecting) {
            this.resumeAutoscrollIfAllowed();
          } else {
            this.stopAutoscroll();
          }
        });
      },
      { threshold: 0.2 }
    );
    this.observer.observe(this);
  }

  /**
   * Returns the pixel distance to scroll for one item
   * (first item width + the gap to the second item).
   */
  getItemStride() {
    if (this.items.length < 2) return this.items[0]?.offsetWidth || 0;
    return this.items[1].offsetLeft - this.items[0].offsetLeft;
  }

  scrollByItems(direction) {
    const stride = this.getItemStride();
    if (!stride) return;

    const currentLeft = this.list.scrollLeft;
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;

    let target = currentLeft + stride * direction;

    // Loop logic: at the end and moving forward → back to start
    // At the start and moving backward → jump to end
    if (direction > 0 && currentLeft >= maxScroll - 4) {
      target = 0;
    } else if (direction < 0 && currentLeft <= 4) {
      target = maxScroll;
    }

    this.list.scrollTo({ left: target, behavior: 'smooth' });
  }

  startAutoscroll() {
    if (this.reducedMotion || !this.autoscrollEnabled) return;
    this.stopAutoscroll();
    this.autoscrollTimer = window.setInterval(() => {
      this.scrollByItems(1);
    }, this.autoscrollSpeed);
  }

  stopAutoscroll() {
    if (this.autoscrollTimer) {
      window.clearInterval(this.autoscrollTimer);
      this.autoscrollTimer = null;
    }
  }

  /**
   * Called whenever user interacts (touch / wheel / arrow click).
   * Pauses autoscroll, then resumes after RESUME_DELAY of inactivity.
   */
  handleUserInteraction() {
    this.stopAutoscroll();
    if (this.resumeTimer) {
      window.clearTimeout(this.resumeTimer);
    }
    this.resumeTimer = window.setTimeout(() => {
      this.resumeAutoscrollIfAllowed();
    }, this.RESUME_DELAY);
  }

  resumeAutoscrollIfAllowed() {
    if (
      this.autoscrollEnabled
      && !this.reducedMotion
      && !this.isPaused
      && this.isVisible
    ) {
      this.startAutoscroll();
    }
  }

  /**
   * Disable prev/next buttons when at the boundaries (for non-looping UX feedback).
   * Note: looping still works via autoscroll, but buttons visually indicate boundary.
   * We keep buttons enabled so user can manually loop too.
   */
  updateArrowStates() {
    // Keep arrows always enabled - loop is allowed in both directions.
    // This method is kept for future enhancement (e.g. show subtle hint at boundary).
  }

  /**
   * Simple throttle for scroll listener.
   */
  throttle(fn, limit) {
    let lastCall = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastCall >= limit) {
        lastCall = now;
        fn.apply(this, args);
      }
    };
  }
}

customElements.define('featured-collections-carousel', FeaturedCollectionsCarousel);
